/**
 * B5FormalizationTemplateEditor.jsx - Sprint 3
 * 
 * Editor del template de formalización para el entregable final de la semana.
 * Soporta 3 formatos: Markdown, JSON, y Structured (constructor de secciones).
 * 
 * @see PLAN_GROUPCOURSE_BUILDER.md - Sprint 3, Día 14
 */

import React, { useState, useEffect } from 'react';
import { 
  FileCheck,
  Plus,
  Trash2,
  AlertCircle,
  Eye,
  Code,
  FileText,
  Layers,
  GripVertical,
  Save
} from 'lucide-react';

const B5FormalizationTemplateEditor = ({ template, onChange }) => {
  const [localTemplate, setLocalTemplate] = useState({
    format: 'structured',
    markdownContent: '',
    jsonSchema: '',
    sections: []
  });

  const [showPreview, setShowPreview] = useState(false);
  const [jsonError, setJsonError] = useState(null);

  // Sincronizar con props
  useEffect(() => {
    if (template) {
      setLocalTemplate({
        format: template.format || 'structured',
        markdownContent: template.markdownContent || '',
        jsonSchema: template.jsonSchema || '',
        sections: (template.sections || []).map(section => ({
          ...section,
          fields: section.fields || []  // Normalizar fields para evitar undefined
        }))
      });
    }
  }, [template]);

  const formatOptions = [
    { 
      value: 'structured', 
      label: 'Estructurado', 
      icon: Layers,
      description: 'Constructor visual de secciones (recomendado)'
    },
    { 
      value: 'markdown', 
      label: 'Markdown', 
      icon: FileText,
      description: 'Texto libre con formato Markdown'
    },
    { 
      value: 'json', 
      label: 'JSON Schema', 
      icon: Code,
      description: 'Esquema JSON personalizado'
    }
  ];

  const sectionFieldTypes = [
    { value: 'text', label: 'Texto corto', icon: '📝' },
    { value: 'textarea', label: 'Texto largo', icon: '📄' },
    { value: 'number', label: 'Número', icon: '🔢' },
    { value: 'select', label: 'Selección', icon: '☑️' },
    { value: 'markdown', label: 'Markdown', icon: '✍️' }
  ];

  const handleUpdate = (field, value) => {
    const updated = { ...localTemplate, [field]: value };
    setLocalTemplate(updated);
    onChange(updated);
  };

  const handleFormatChange = (format) => {
    handleUpdate('format', format);
  };

  // ===== STRUCTURED FORMAT HANDLERS =====
  const addSection = () => {
    const newSection = {
      id: Date.now().toString(),
      title: '',
      description: '',
      required: true,
      fields: []
    };
    handleUpdate('sections', [...localTemplate.sections, newSection]);
  };

  const updateSection = (sectionId, field, value) => {
    const updated = localTemplate.sections.map(section =>
      section.id === sectionId ? { ...section, [field]: value } : section
    );
    handleUpdate('sections', updated);
  };

  const deleteSection = (sectionId) => {
    handleUpdate('sections', localTemplate.sections.filter(s => s.id !== sectionId));
  };

  const addFieldToSection = (sectionId) => {
    const newField = {
      id: Date.now().toString(),
      label: '',
      type: 'text',
      placeholder: '',
      required: false,
      options: []
    };
    const updated = localTemplate.sections.map(section =>
      section.id === sectionId 
        ? { ...section, fields: [...(section.fields || []), newField] }
        : section
    );
    handleUpdate('sections', updated);
  };

  const updateSectionField = (sectionId, fieldId, fieldKey, value) => {
    const updated = localTemplate.sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          fields: (section.fields || []).map(field =>
            field.id === fieldId ? { ...field, [fieldKey]: value } : field
          )
        };
      }
      return section;
    });
    handleUpdate('sections', updated);
  };

  const deleteSectionField = (sectionId, fieldId) => {
    const updated = localTemplate.sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          fields: (section.fields || []).filter(f => f.id !== fieldId)
        };
      }
      return section;
    });
    handleUpdate('sections', updated);
  };

  // ===== JSON VALIDATION =====
  const validateJSON = (jsonString) => {
    try {
      JSON.parse(jsonString);
      setJsonError(null);
      return true;
    } catch (error) {
      setJsonError(error.message);
      return false;
    }
  };

  const handleJSONChange = (value) => {
    handleUpdate('jsonSchema', value);
    if (value.trim()) {
      validateJSON(value);
    } else {
      setJsonError(null);
    }
  };

  // ===== PREVIEW RENDERING =====
  const renderPreview = () => {
    if (localTemplate.format === 'markdown') {
      return (
        <div className="prose prose-sm max-w-none">
          <pre className="whitespace-pre-wrap bg-gray-50 p-4 rounded border">
            {localTemplate.markdownContent || 'Sin contenido...'}
          </pre>
        </div>
      );
    }

    if (localTemplate.format === 'json') {
      if (jsonError) {
        return (
          <div className="text-red-600 bg-red-50 p-4 rounded border border-red-200">
            <p className="font-semibold">Error de JSON:</p>
            <p className="text-sm">{jsonError}</p>
          </div>
        );
      }
      return (
        <pre className="bg-gray-50 p-4 rounded border text-sm overflow-auto">
          {localTemplate.jsonSchema || '{}'}
        </pre>
      );
    }

    if (localTemplate.format === 'structured') {
      if (localTemplate.sections.length === 0) {
        return (
          <div className="text-center text-gray-500 py-8">
            No hay secciones definidas. Agrega al menos una sección.
          </div>
        );
      }
      return (
        <div className="space-y-4">
          {localTemplate.sections.map((section, idx) => (
            <div key={section.id} className="border border-gray-200 rounded-lg p-4 bg-white">
              <h3 className="font-semibold text-gray-900">
                {section.title || `Sección ${idx + 1}`}
                {section.required && <span className="text-red-500 ml-1">*</span>}
              </h3>
              {section.description && (
                <p className="text-sm text-gray-600 mt-1">{section.description}</p>
              )}
              <div className="mt-3 space-y-2">
                {(section.fields || []).map(field => (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-gray-700">
                      {field.label || 'Campo sin nombre'}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {field.type === 'textarea' || field.type === 'markdown' ? (
                      <textarea
                        placeholder={field.placeholder}
                        rows={3}
                        className="mt-1 w-full px-3 py-2 border rounded-lg"
                        disabled
                      />
                    ) : field.type === 'select' ? (
                      <select className="mt-1 w-full px-3 py-2 border rounded-lg" disabled>
                        <option>{field.placeholder || 'Selecciona una opción'}</option>
                        {field.options?.map((opt, i) => (
                          <option key={i}>{opt}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type === 'number' ? 'number' : 'text'}
                        placeholder={field.placeholder}
                        className="mt-1 w-full px-3 py-2 border rounded-lg"
                        disabled
                      />
                    )}
                  </div>
                ))}
                {(section.fields || []).length === 0 && (
                  <p className="text-sm text-gray-400 italic">Sin campos definidos</p>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header informativo */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-start gap-3">
        <FileCheck className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-indigo-900 mb-1">
            Template de Formalización
          </h3>
          <p className="text-sm text-indigo-700">
            Define la estructura del entregable <strong>final formalizado</strong> que los 
            estudiantes deben completar. Puede ser un documento estructurado, Markdown libre o JSON.
          </p>
        </div>
      </div>

      {/* Selector de Formato */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          Formato del Template
          <span className="text-red-500 ml-1">*</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {formatOptions.map(format => {
            const Icon = format.icon;
            return (
              <label
                key={format.value}
                className={`flex flex-col gap-2 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  localTemplate.format === format.value
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-indigo-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="format"
                    value={format.value}
                    checked={localTemplate.format === format.value}
                    onChange={(e) => handleFormatChange(e.target.value)}
                    className="w-4 h-4 text-indigo-600"
                  />
                  <Icon className="w-5 h-5 text-gray-700" />
                  <span className="font-semibold text-gray-900">{format.label}</span>
                </div>
                <p className="text-xs text-gray-600 ml-6">{format.description}</p>
              </label>
            );
          })}
        </div>
      </div>

      {/* Editor según formato */}
      <div className="border border-gray-200 rounded-lg p-6 bg-white">
        {/* MARKDOWN EDITOR */}
        {localTemplate.format === 'markdown' && (
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Contenido Markdown
            </label>
            <textarea
              value={localTemplate.markdownContent}
              onChange={(e) => handleUpdate('markdownContent', e.target.value)}
              placeholder="# Entregable Final&#10;&#10;## Descripción del Proyecto&#10;Describe brevemente tu proyecto...&#10;&#10;## Resultados Obtenidos&#10;..."
              rows={15}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm resize-none"
            />
            <p className="text-sm text-gray-500 mt-2">
              Usa Markdown para formatear el contenido. Los estudiantes verán este template al formalizar.
            </p>
          </div>
        )}

        {/* JSON EDITOR */}
        {localTemplate.format === 'json' && (
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              JSON Schema
            </label>
            <textarea
              value={localTemplate.jsonSchema}
              onChange={(e) => handleJSONChange(e.target.value)}
              placeholder='{"title": "string", "description": "string", "sections": []}'
              rows={15}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm resize-none ${
                jsonError ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
            />
            {jsonError && (
              <div className="mt-2 flex items-start gap-2 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>Error de sintaxis JSON: {jsonError}</p>
              </div>
            )}
            <p className="text-sm text-gray-500 mt-2">
              Define un esquema JSON válido para el entregable formalizado.
            </p>
          </div>
        )}

        {/* STRUCTURED EDITOR */}
        {localTemplate.format === 'structured' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                Secciones del Entregable
              </h3>
              <button
                onClick={addSection}
                className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Agregar Sección
              </button>
            </div>

            {localTemplate.sections.length === 0 && (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <FileCheck className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No hay secciones definidas</p>
                <p className="text-sm text-gray-500 mt-1">
                  Haz clic en "Agregar Sección" para empezar
                </p>
              </div>
            )}

            {localTemplate.sections.map((section, sectionIdx) => (
              <div key={section.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-start gap-3 mb-4">
                  <GripVertical className="w-5 h-5 text-gray-400 mt-2 cursor-move" />
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start gap-3">
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                        placeholder={`Sección ${sectionIdx + 1}: Título`}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-semibold"
                      />
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={section.required}
                          onChange={(e) => updateSection(section.id, 'required', e.target.checked)}
                          className="w-4 h-4 text-indigo-600"
                        />
                        Requerida
                      </label>
                      <button
                        onClick={() => deleteSection(section.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    <textarea
                      value={section.description}
                      onChange={(e) => updateSection(section.id, 'description', e.target.value)}
                      placeholder="Descripción de la sección (opcional)"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />

                    {/* Nota informativa: cada sección es un área de texto colaborativa */}
                    <p className="text-xs text-indigo-600 bg-indigo-50 rounded px-3 py-2 mt-1">
                      Los estudiantes completarán esta sección con un área de texto grupal.
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Toggle Preview */}
      <div className="flex items-center justify-between border-t pt-4">
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium"
        >
          <Eye className="w-5 h-5" />
          {showPreview ? 'Ocultar' : 'Mostrar'} Vista Previa
        </button>
      </div>

      {/* Preview */}
      {showPreview && (
        <div className="border border-gray-300 rounded-lg p-6 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Vista Previa del Template
          </h3>
          {renderPreview()}
        </div>
      )}
    </div>
  );
};

export default B5FormalizationTemplateEditor;
